import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import Modal from '../common/Modal';
import { Customer } from '../../types/customer';
import {
  CurrencyDollarIcon,
  PlusIcon,
  TrashIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import toast from 'react-hot-toast';
import { DatePicker } from '../common/DatePicker';

interface PricingData {
  year: number;
  price: string;
  currency: string;
  validFrom: string;
  validTo: string;
  status: 'active' | 'future' | 'planned' | 'expired';
  consumption_kwh?: string;
  total_amount_huf?: string;
  pod_id?: string;
  notes?: string;
}

interface PricingFormData {
  currentPricing?: PricingData;
  futurePricing: PricingData[];
  hasFuturePricing: boolean;
}

interface PricingManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
  onUpdate?: (customerId: string) => void;
}

const PricingManagementModal: React.FC<PricingManagementModalProps> = ({
  isOpen,
  onClose,
  customer,
  onUpdate
}) => {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'current' | 'future'>('current');

  const defaultPricingData: PricingData = {
    year: new Date().getFullYear(),
    price: '',
    currency: 'HUF',
    validFrom: '',
    validTo: '',
    status: 'planned',
    consumption_kwh: '',
    total_amount_huf: '',
    pod_id: '',
    notes: ''
  };

  const { register, control, handleSubmit, watch, setValue, reset, getValues } = useForm<PricingFormData>({
    defaultValues: {
      currentPricing: undefined,
      futurePricing: [],
      hasFuturePricing: false
    }
  });

  const { fields: futurePricingFields, append: appendFuturePricing, remove: removeFuturePricing } = useFieldArray({
    control,
    name: 'futurePricing'
  });

  // Initialize form data when customer changes
  useEffect(() => {
    if (customer && isOpen) {
      const currentPricing = customer.currentPricing || undefined;
      const futurePricing = customer.futurePricing || [];
      
      reset({
        currentPricing,
        futurePricing,
        hasFuturePricing: customer.hasFuturePricing || false
      });
    }
  }, [customer, isOpen, reset]);

  const onSubmit = async (data: PricingFormData) => {
    if (!customer) return;

    try {
      setIsLoading(true);

      // Prepare update data
      const updateData: any = {
        currentPricing: data.currentPricing || null,
        futurePricing: data.futurePricing || [],
        hasFuturePricing: data.futurePricing.length > 0,
        updatedAt: serverTimestamp()
      };

      // Update pricing structure
      if (data.currentPricing || data.futurePricing.length > 0) {
        updateData.pricingStructure = {
          lastPricingUpdate: new Date().toISOString(),
          pricingHistory: customer.pricingStructure?.pricingHistory || []
        };
      }

      // Update customer in Firebase
      const customerRef = doc(db, 'customers', customer.id);
      await updateDoc(customerRef, updateData);

      toast.success('Árazási adatok sikeresen frissítve!');
      
      // Call update callback
      if (onUpdate) {
        onUpdate(customer.id);
      }
      
      onClose();
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error('Hiba történt az árazási adatok frissítése során');
    } finally {
      setIsLoading(false);
    }
  };

  const addFuturePricing = () => {
    const nextYear = new Date().getFullYear() + 1;
    appendFuturePricing({
      ...defaultPricingData,
      year: nextYear,
      status: 'future'
    });
  };

  const calculateTotalAmount = (price: string, consumption: string) => {
    const priceNum = parseFloat(price.replace(/[^0-9.]/g, ''));
    const consumptionNum = parseFloat(consumption.replace(/[^0-9.]/g, ''));
    
    if (priceNum && consumptionNum) {
      return (priceNum * consumptionNum).toFixed(2);
    }
    return '';
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircleIcon, text: 'Aktív' },
      future: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: CalendarDaysIcon, text: 'Jövőbeli' },
      planned: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: ExclamationTriangleIcon, text: 'Tervezett' },
      expired: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: XCircleIcon, text: 'Lejárt' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.planned;
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    );
  };

  if (!customer) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`💰 Árazás kezelése - ${customer.companyName || `${customer.firstName} ${customer.lastName}`}`}
      size="4xl"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              type="button"
              onClick={() => setActiveTab('current')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'current'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📅 Jelenlegi árazás
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('future')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'future'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🔮 Jövőbeli árazások ({futurePricingFields.length})
            </button>
          </nav>
        </div>

        {/* Current Pricing Tab */}
        {activeTab === 'current' && (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-green-800 mb-4">📅 Jelenlegi árazás beállítása</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Év
                  </label>
                  <input
                    type="number"
                    {...register('currentPricing.year')}
                    min="2020"
                    max="2030"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ár
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      {...register('currentPricing.price')}
                      className="flex-1 border border-gray-300 rounded-l-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder="49.7"
                    />
                    <select
                      {...register('currentPricing.currency')}
                      className="border border-l-0 border-gray-300 rounded-r-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="HUF">HUF</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Érvényesség kezdete
                  </label>
                  <input
                    type="date"
                    {...register('currentPricing.validFrom')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Érvényesség vége
                  </label>
                  <input
                    type="date"
                    {...register('currentPricing.validTo')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Státusz
                  </label>
                  <select
                    {...register('currentPricing.status')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="active">Aktív</option>
                    <option value="planned">Tervezett</option>
                    <option value="expired">Lejárt</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    POD ID
                  </label>
                  <input
                    type="text"
                    {...register('currentPricing.pod_id')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="HU000210F11-E649886239770-1000001"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Megjegyzések
                </label>
                <textarea
                  {...register('currentPricing.notes')}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="További információk az árazásról..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Future Pricing Tab */}
        {activeTab === 'future' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-800">🔮 Jövőbeli árazások</h3>
              <button
                type="button"
                onClick={addFuturePricing}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Új árazás hozzáadása
              </button>
            </div>

            {futurePricingFields.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <CurrencyDollarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Még nincs jövőbeli árazás beállítva</p>
                <button
                  type="button"
                  onClick={addFuturePricing}
                  className="mt-4 text-primary-600 hover:text-primary-700"
                >
                  Első árazás hozzáadása
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {futurePricingFields.map((field, index) => (
                  <div key={field.id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-md font-medium text-blue-800">
                        📅 Árazás #{index + 1}
                      </h4>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(watch(`futurePricing.${index}.status`) || 'planned')}
                        <button
                          type="button"
                          onClick={() => removeFuturePricing(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Év
                        </label>
                        <input
                          type="number"
                          {...register(`futurePricing.${index}.year`)}
                          min="2024"
                          max="2030"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ár
                        </label>
                        <div className="flex">
                          <input
                            type="text"
                            {...register(`futurePricing.${index}.price`)}
                            className="flex-1 border border-gray-300 rounded-l-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            placeholder="49.7"
                          />
                          <select
                            {...register(`futurePricing.${index}.currency`)}
                            className="border border-l-0 border-gray-300 rounded-r-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="HUF">HUF</option>
                            <option value="EUR">EUR</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Státusz
                        </label>
                        <select
                          {...register(`futurePricing.${index}.status`)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        >
                          <option value="future">Jövőbeli</option>
                          <option value="planned">Tervezett</option>
                          <option value="active">Aktív</option>
                          <option value="expired">Lejárt</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Érvényesség kezdete
                        </label>
                        <input
                          type="date"
                          {...register(`futurePricing.${index}.validFrom`)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Érvényesség vége
                        </label>
                        <input
                          type="date"
                          {...register(`futurePricing.${index}.validTo`)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Fogyasztás (kWh)
                        </label>
                        <input
                          type="text"
                          {...register(`futurePricing.${index}.consumption_kwh`)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          placeholder="1234"
                        />
                      </div>

                      <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          POD ID
                        </label>
                        <input
                          type="text"
                          {...register(`futurePricing.${index}.pod_id`)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          placeholder="HU000210F11-E649886239770-1000001"
                        />
                      </div>

                      <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Megjegyzések
                        </label>
                        <textarea
                          {...register(`futurePricing.${index}.notes`)}
                          rows={2}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          placeholder="További információk..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            disabled={isLoading}
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Mentés...' : 'Árazás mentése'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default PricingManagementModal; 